// Paper styles for canvas notes (plan §3.5). Pure CSS layered BEHIND the ink:
// ruled/grid draw thin repeating lines, dots a repeating dot-grid, blank draws
// nothing. The line colour is a single neutral grey at low alpha — the same one
// the infinite dot-grid has always used — so it stays subtle and ink-friendly on
// every background in the note palette (paper white → near-black).

export type PaperStyle = "blank" | "ruled" | "grid" | "dots";

export const PAPER_STYLES: readonly PaperStyle[] = ["blank", "ruled", "grid", "dots"];

/** One paper cell, in a canvas page's VIRTUAL (base) units. Multiplied by the
 * committed page/camera scale before it reaches {@link paperBackground}, so the
 * lines track the page as it zooms. */
export const PAPER_UNIT = 56;

// A neutral 50%-grey hairline: readable on a white page yet unobtrusive on a dark
// one, and never competing with coloured ink. Matches the legacy `.gv-canvas-grid`.
const LINE = "rgba(128, 128, 128, 0.28)";

/** Normalize an unknown/legacy paper value to a real style (missing ⇒ blank),
 * mirroring how a null `canvas_layout` is treated as "pages". */
export function normalizePaper(value: string | null | undefined): PaperStyle {
  return (PAPER_STYLES as readonly string[]).includes(value ?? "")
    ? (value as PaperStyle)
    : "blank";
}

/** Normalize paper for an INFINITE canvas. F7 shipped the dot-grid as the infinite
 * surface's signature look, and the feature is unreleased (no real legacy), so a
 * missing/unknown value defaults to "dots" — NOT "blank" like the paged view. An
 * explicit style (including "blank") is honoured, so a user can still opt out. */
export function normalizeInfinitePaper(value: string | null | undefined): PaperStyle {
  return (PAPER_STYLES as readonly string[]).includes(value ?? "")
    ? (value as PaperStyle)
    : "dots";
}

/** How far the spacing control may stretch or squeeze a paper's cell, as a
 * multiple of {@link PAPER_UNIT}. Wide enough to go from tight graph paper to
 * generous handwriting rules, narrow enough that neither end is unusable. */
export const MIN_PAPER_SPACING = 0.4;
export const MAX_PAPER_SPACING = 3;
export const DEFAULT_PAPER_SPACING = 1;

/** Clamp a stored/typed spacing multiplier; anything unusable reads as the
 * default rather than a surface with no lines or a solid block of them. */
export function normalizeSpacing(value: number | null | undefined): number {
  const n = typeof value === "number" && Number.isFinite(value) ? value : DEFAULT_PAPER_SPACING;
  return Math.min(MAX_PAPER_SPACING, Math.max(MIN_PAPER_SPACING, n));
}

/** The CSS `background-image` + `background-size` for a paper style, with one cell
 * = `unitPx` and a line `linePx` thick — BOTH in whatever space the layer lives
 * in. Returns null for a blank surface so callers can clear the layer.
 *
 * `linePx` exists because of zoom jitter. A repeating gradient is rasterized by
 * the browser, so rewriting its size every animation frame re-rasterizes it every
 * frame, and a 1px hard stop lands on a different sub-pixel boundary each time —
 * which is the crawling, shimmering grid you see while pinching. The cure is to
 * put the pattern INSIDE the camera-transformed layer, where its size is constant
 * in scene units and the compositor does the scaling, and to re-derive only the
 * line width when the gesture settles. That makes this a per-gesture write
 * instead of a per-frame one.
 *
 * The horizontal axis of ruled is `100%` (full-width lines); grid and dots tile
 * square cells. Consumers own the `background-position` and the
 * `background-color` beneath. */
export function paperBackground(
  paper: PaperStyle,
  unitPx: number,
  linePx = 1,
): { backgroundImage: string; backgroundSize: string } | null {
  const u = Math.max(1, unitPx);
  // A hairline that has fallen below a device pixel renders as nothing at all on
  // some engines, so keep a floor.
  const w = Math.max(0.35, linePx);
  const dot = w * 1.1;
  switch (paper) {
    case "ruled":
      return {
        backgroundImage: `repeating-linear-gradient(to bottom, ${LINE} 0, ${LINE} ${w}px, transparent ${w}px, transparent ${u}px)`,
        backgroundSize: `100% ${u}px`,
      };
    case "grid":
      return {
        backgroundImage:
          `repeating-linear-gradient(to right, ${LINE} 0, ${LINE} ${w}px, transparent ${w}px, transparent ${u}px), ` +
          `repeating-linear-gradient(to bottom, ${LINE} 0, ${LINE} ${w}px, transparent ${w}px, transparent ${u}px)`,
        backgroundSize: `${u}px ${u}px`,
      };
    case "dots":
      return {
        backgroundImage: `radial-gradient(circle, ${LINE} ${dot}px, transparent ${dot * 1.5}px)`,
        backgroundSize: `${u}px ${u}px`,
      };
    default:
      return null;
  }
}

/** Is a page's fill colour LIGHT? Drives adaptive ink — a pen on a dark canvas
 * has to lay down light ink to be legible, and vice versa. Perceived luminance
 * (Rec. 709); a malformed/absent colour reads as light, because every surface
 * but a deliberately dark canvas note is paper-white. */
export function isLightPaper(hex: string | null | undefined): boolean {
  if (typeof hex !== "string") return true;
  const v = hex.trim().replace("#", "");
  const full = v.length === 3 ? [...v].map((c) => c + c).join("") : v;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return true;
  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.5;
}
